/* eslint-disable camelcase, no-param-reassign */
import React, {
  useMemo,
  useState,
} from 'react';
import * as Yup from 'yup';
import {
  Formik,
  Field,
} from 'formik';
import {
  useDispatch,
} from 'react-redux';
import {
  toast,
} from 'react-toastify';

import {
  coreActions,
  Namespace,
} from '@gymcloud/core';

import {
  Spinner,
  TextField,
  CheckboxField,
  PasswordField,
} from 'web-components';
import {
  Flex,
} from 'web-styled';

import * as S from '../styled';

const validationSchema = Yup.object().shape({
  first_name: Yup.string()
    .required('First Name is required'),
  last_name: Yup.string()
    .required('Last Name is required'),
  email: Yup.string()
    .email('Email must be a valid email')
    .required('Email is required'),
  password: Yup.string()
    .min(6)
    .required('Password is required'),
  password_confirmation: Yup.string()
    .required('Confirm password is required')
    .oneOf([Yup.ref('password'), null], 'Passwords do not match'),
  agreement: Yup.bool().nullable(true)
    .oneOf([null, true], 'Must Accept Terms and Conditions'),
});

type Props = {
  isTest: boolean,
  userData: any,
  namespace: Namespace,
  buttonText: string,
  onSubmit: { (user: any): void },
};

const StepOneForm = ({
  isTest,
  userData,
  buttonText,
  namespace,
  onSubmit,
}: Props) => {
  const [isLoading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const agreement = useMemo(() => ({
    strongfirst: {
      url: 'https://www.strongfirst.com/privacy-policy/',
      name: 'StrongFirst Privacy Policy',
      has_email_subscription: 'Subscribe me to the StrongFirst newsletter for the latest news, articles, and podcasts',
    },
  }[namespace.symbol] || {}), [namespace.symbol]);

  function checkEmail(email) {
    return new Promise((resolve) => {
      dispatch(
        coreActions.checkEmail({
          email,
          callbacks: {
            onError: () => {
              resolve(true);
            },
            onSuccess: () => {
              resolve(false);
            },
          },
        }),
      );
    });
  }

  function createUser({ user, profile }, callbacks?: {}) {
    return new Promise((resolve, reject) => {
      dispatch(
        coreActions.requestCreateUser({
          data: {
            user,
            profile,
          },
          callbacks: {
            ...callbacks,
            onSuccess: resolve,
            onError: reject,
          },
        }),
      );
    });
  }

  const handleSubmit = async (values) => {
    setLoading(true);
    const hasEmail = await checkEmail(values.email);
    if (hasEmail) {
      toast.error(
        'Email already exists in GymCloud database. Use a different email or contact GymCloud support.',
      );
      setLoading(false);
      return false;
    }

    if (isTest) {
      await createUser({
        user: {
          ...values,
          signup_source: 'desktop',
        },
        profile: {
          first_name: values.first_name,
          last_name: values.last_name,
        },
      });
    }

    setLoading(false);
    onSubmit(values);

    return true;
  };

  return (
    <Formik
      initialValues={{
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_confirmation: '',
        namespace: namespace.symbol,
        is_pro: false,
        is_client: true,
        ...userData,
        has_email_subscription: agreement.has_email_subscription ? true : null,
        agreement: agreement.name ? false : null,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <S.Form column onSubmit={formikProps.handleSubmit}>
          {isLoading
            ? (
              <Spinner />
            ) : (
              <Flex column width="100%" spacing={15}>
                <Field
                  type="text"
                  name="first_name"
                  label="First Name*"
                  color="#121827"
                  component={TextField}
                  placeholder="e.g. John"
                />
                <Field
                  type="text"
                  name="last_name"
                  label="Last Name*"
                  color="#121827"
                  component={TextField}
                  placeholder="e.g Smith"
                />
                <Field
                  type="email"
                  name="email"
                  label="Email*"
                  color="#121827"
                  component={TextField}
                  placeholder="Enter email"
                />
                <Field
                  name="password"
                  label="Password*"
                  color="#121827"
                  component={PasswordField}
                  placeholder="Password"
                  autoComplete="off"
                />
                <Field
                  name="password_confirmation"
                  label="Confirm Password*"
                  color="#121827"
                  component={PasswordField}
                  placeholder="Password"
                  autoComplete="off"
                />
                {agreement.url && (
                  <Field
                    name="agreement"
                    label={(
                      <span>
                        {'I agree to the '}
                        <a
                          href={agreement.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {agreement.name}
                        </a>
                        {' and '}
                        <a
                          href="https://www.gymcloud.com/legal/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          GymCloud End User Agreement
                        </a>
                      </span>
                    )}
                    component={CheckboxField}
                  />
                )}
                {agreement.has_email_subscription && (
                  <Field
                    name="has_email_subscription"
                    align="flex-start"
                    label={(
                      <span>
                        {agreement.has_email_subscription}
                      </span>
                    )}
                    component={CheckboxField}
                  />
                )}
                <S.SubmitButton
                  isLoading={formikProps.isSubmitting}
                  isDisabled={formikProps.isSubmitting}
                  onClick={() => {
                    formikProps.validateForm().then(errors => {
                      Object.keys(errors)
                        .reverse()
                        .forEach(key => {
                          toast.error(errors[key]);
                        });
                    });
                  }}
                >
                  {isTest ? 'Continue' : buttonText}
                </S.SubmitButton>
              </Flex>
            )}
        </S.Form>
      )}
    </Formik>
  );
};

export default StepOneForm;
